import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Phone,
  Calendar as CalendarIcon,
  Plus,
  Search,
  Mail,
  FileText,
  Palette,
  Sparkles,
  PhoneIncoming,
  PhoneOutgoing,
  CheckCircle2,
  Circle,
  Trash2,
  Bell,
  Inbox,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { plannedActivitiesApi, callOutcomesApi } from "../../lib/plannedActivitiesApi";
import CallLogDialog from "./CallLogDialog";
import ActivityPlanDialog from "./ActivityPlanDialog";
import { nameInitials } from "../../lib/nameInitials";
import useTenantWebSocket from "../../hooks/useTenantWebSocket";

const RANGE_CHIPS = [
  { key: "today", label: "Bugün", tone: "bg-red-600 text-white" },
  { key: "week", label: "Bu Hafta", tone: "bg-red-600 text-white" },
  { key: "month", label: "Bu Ay", tone: "bg-red-600 text-white" },
  { key: "future", label: "Gelecek", tone: "bg-red-600 text-white" },
  { key: "all", label: "Tümü", tone: "bg-red-600 text-white" },
];

const TYPE_META = {
  email:  { label: "E-posta",  Icon: Mail,     tone: "bg-blue-50 text-blue-700 border-blue-200" },
  phone:  { label: "Telefon",  Icon: Phone,    tone: "bg-sky-50 text-sky-700 border-sky-200" },
  quote:  { label: "Teklif",   Icon: FileText, tone: "bg-amber-50 text-amber-700 border-amber-200" },
  design: { label: "Tasarım",  Icon: Palette,  tone: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" },
  custom: { label: "Özel",     Icon: Sparkles, tone: "bg-zinc-50 text-zinc-700 border-zinc-200" },
};

const PRIORITY_META = {
  high:   { label: "Yüksek", tone: "bg-red-100 text-red-700" },
  normal: { label: "Normal", tone: "bg-blue-100 text-blue-700" },
  low:    { label: "Düşük",  tone: "bg-zinc-100 text-zinc-600" },
};

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * "Aktiviteler" tab content for a construction tracking row.
 * Two quick-add cards on top (Görüşme Kaydı / Aktivite Planlama),
 * then filter chips + search + type dropdown, then the list.
 */
export default function PlannedActivitiesTab({
  constructionId,
  contacts,
  companies,
  defaultCompanyId,
  users,
  currentUserId,
  companyName,
}) {
  const [activities, setActivities] = useState([]);
  const [counts, setCounts] = useState({ today: 0, week: 0, month: 0, future: 0, all: 0, completed: 0 });
  const [outcomes, setOutcomes] = useState([]);
  const [contactMap, setContactMap] = useState({});
  const [range, setRange] = useState("week");
  const [showCompleted, setShowCompleted] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);

  useEffect(() => {
    setContactMap(
      Object.fromEntries(
        (contacts || []).map((c) => [
          c.id,
          [c.first_name, c.last_name].filter(Boolean).join(" ") ||
            c.email ||
            "İsimsiz kişi",
        ])
      )
    );
  }, [contacts]);

  useEffect(() => {
    // Preload outcome labels for rendering in row badges
    callOutcomesApi.list().then(setOutcomes).catch(() => setOutcomes([]));
  }, []);

  const outcomeMap = useMemo(
    () => Object.fromEntries(outcomes.map((o) => [o.id, o])),
    [outcomes]
  );

  const userMap = useMemo(
    () => Object.fromEntries((users || []).map((u) => [u.id, u])),
    [users]
  );

  // Split into two visually distinct groups — the user asked for the
  // "Görüşme Kayıtları" and "Planlı Aktiviteler" to live in separate
  // sections so a past call log is never confused with a to-do item.
  const plannedList = useMemo(
    () => activities.filter((a) => a.kind === "planned"),
    [activities]
  );
  const callList = useMemo(
    () => activities.filter((a) => a.kind === "call"),
    [activities]
  );

  const load = useCallback(async () => {
    if (!constructionId) return;
    setLoading(true);
    try {
      const [list, counters] = await Promise.all([
        plannedActivitiesApi.list(constructionId, {
          range,
          show_completed: showCompleted,
          activity_type: typeFilter || undefined,
          q: search.trim() || undefined,
        }),
        plannedActivitiesApi.counts(constructionId),
      ]);
      setActivities(list);
      setCounts(counters);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [constructionId, range, showCompleted, typeFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  // Realtime — refresh whenever any tab (mine or a teammate's) creates,
  // updates or deletes an activity on this same construction row. The
  // guard on `construction_id` prevents unrelated activity pings from
  // spamming refetches.
  useTenantWebSocket((msg) => {
    if (
      msg?.event === "planned_activity_changed" &&
      msg.construction_id === constructionId
    ) {
      load();
    }
  });

  const toggleCompleted = async (a) => {
    try {
      await plannedActivitiesApi.update(a.id, { completed: !a.completed });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Güncellenemedi");
    }
  };

  const removeActivity = async (a) => {
    if (!window.confirm(`"${a.title || "Bu aktivite"}" silinsin mi?`)) return;
    try {
      await plannedActivitiesApi.remove(a.id);
      toast.success("Silindi");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="space-y-5">
      {/* Header + big quick-add cards */}
      <section className="rounded-xl border border-zinc-200 bg-white p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="flex items-center gap-1.5 text-base font-semibold text-zinc-900">
              <CalendarIcon className="h-4 w-4 text-violet-600" />
              Aktivite Timeline
            </h3>
            <p className="text-xs text-zinc-500">{companyName || "Kayıt"}</p>
          </div>
        </div>

        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">
          + Yeni Aktivite
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setCallOpen(true)}
            data-testid="add-call-log-card"
            className="group flex flex-col items-center gap-2 rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-sky-100/40 p-6 transition-all hover:border-sky-400 hover:shadow-md"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sky-500/10 text-sky-600 transition-colors group-hover:bg-sky-500/20">
              <Phone className="h-5 w-5" />
            </span>
            <span className="font-semibold text-sky-800">Görüşme Kaydı</span>
            <span className="text-[11px] text-sky-700/70">
              Yapılan telefon görüşmesini kaydet
            </span>
          </button>
          <button
            type="button"
            onClick={() => setPlanOpen(true)}
            data-testid="add-activity-plan-card"
            className="group flex flex-col items-center gap-2 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100/40 p-6 transition-all hover:border-emerald-400 hover:shadow-md"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 transition-colors group-hover:bg-emerald-500/20">
              <CalendarIcon className="h-5 w-5" />
            </span>
            <span className="font-semibold text-emerald-800">Aktivite Planlama</span>
            <span className="text-[11px] text-emerald-700/70">
              Gelecek için görev planla + hatırlat
            </span>
          </button>
        </div>
      </section>

      {/* All activities list */}
      <section className="rounded-xl border border-zinc-200 bg-white p-5">
        <div className="mb-4 flex items-center gap-1.5">
          <Inbox className="h-4 w-4 text-zinc-500" />
          <h3 className="text-base font-semibold text-zinc-900">Tüm Aktiviteler</h3>
        </div>

        {/* Filters */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5">
            {RANGE_CHIPS.map((c) => {
              const n = counts[c.key];
              const active = range === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setRange(c.key)}
                  data-testid={`range-chip-${c.key}`}
                  className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                    active
                      ? "bg-red-600 text-white shadow"
                      : "bg-white text-zinc-700 border border-zinc-200 hover:border-red-300 hover:text-red-700"
                  }`}
                >
                  {c.label}
                  {n > 0 && (
                    <span
                      className={`inline-flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold ${
                        active ? "bg-white/25 text-white" : "bg-zinc-100 text-zinc-700"
                      }`}
                    >
                      {n}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Ara..."
                data-testid="activities-search"
                className="h-8 w-52 pl-7 text-xs"
              />
            </div>
            <Select value={typeFilter || "__all__"} onValueChange={(v) => setTypeFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-8 w-32 text-xs" data-testid="activities-type-filter">
                <SelectValue placeholder="Tümü" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tümü</SelectItem>
                {Object.entries(TYPE_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* List — split into two groups per user requirement:
              "Görüşme kayıtları ile aktiviteler farklı gruplarda gösterilmeli". */}
        {loading ? (
          <div className="flex items-center justify-center py-10 text-zinc-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Yükleniyor…
          </div>
        ) : activities.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-zinc-200 bg-zinc-50/50 p-10 text-center">
            <Inbox className="h-8 w-8 text-zinc-400" />
            <div className="text-sm font-medium text-zinc-700">Aktivite bulunamadı</div>
            <div className="text-xs text-zinc-500">
              Yukarıdaki &quot;Görüşme Kaydı&quot; veya &quot;Aktivite Planlama&quot; kartlarını kullan.
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {plannedList.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  Planlı Aktiviteler
                  <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">
                    {plannedList.length}
                  </span>
                </div>
                <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-100 bg-zinc-50/30">
                  {plannedList.map((a) => (
                    <ActivityRow
                      key={a.id}
                      activity={a}
                      outcomeMap={outcomeMap}
                      contactMap={contactMap}
                      userMap={userMap}
                      onToggleCompleted={() => toggleCompleted(a)}
                      onDelete={() => removeActivity(a)}
                    />
                  ))}
                </ul>
              </div>
            )}

            {callList.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-sky-700">
                  <Phone className="h-3.5 w-3.5" />
                  Görüşme Kayıtları
                  <span className="ml-1 rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800">
                    {callList.length}
                  </span>
                  <span className="ml-1 text-[10px] font-normal normal-case tracking-normal text-zinc-500">
                    · bilgilendirme kayıtları
                  </span>
                </div>
                <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-100 bg-zinc-50/30">
                  {callList.map((a) => (
                    <ActivityRow
                      key={a.id}
                      activity={a}
                      outcomeMap={outcomeMap}
                      contactMap={contactMap}
                      userMap={userMap}
                      onDelete={() => removeActivity(a)}
                    />
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Bottom controls — only meaningful for planned activities. */}
        <div className="mt-4 flex items-center gap-2 border-t border-zinc-100 pt-3">
          <Checkbox
            id="show-completed"
            checked={showCompleted}
            onCheckedChange={setShowCompleted}
            data-testid="show-completed-toggle"
          />
          <label htmlFor="show-completed" className="cursor-pointer text-xs text-zinc-600">
            Tamamlanan planlı aktiviteleri göster
          </label>
          {counts.completed > 0 && (
            <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
              {counts.completed} tamamlanmış
            </span>
          )}
        </div>
      </section>

      {/* Dialogs */}
      <CallLogDialog
        open={callOpen}
        onClose={() => setCallOpen(false)}
        constructionId={constructionId}
        contacts={contacts}
        companies={companies}
        defaultCompanyId={defaultCompanyId}
        users={users}
        currentUserId={currentUserId}
        onCreated={load}
      />
      <ActivityPlanDialog
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        constructionId={constructionId}
        users={users}
        currentUserId={currentUserId}
        onCreated={load}
      />
    </div>
  );
}

function ActivityRow({ activity: a, outcomeMap, contactMap, userMap, onToggleCompleted, onDelete }) {
  const isCall = a.kind === "call";
  const typeMeta = isCall
    ? { label: "Telefon", Icon: Phone, tone: "bg-sky-50 text-sky-700 border-sky-200" }
    : TYPE_META[a.activity_type] || TYPE_META.custom;
  const outcome = a.call_outcome_id ? outcomeMap[a.call_outcome_id] : null;
  const contactName = a.call_person_contact_id
    ? contactMap[a.call_person_contact_id]
    : null;
  // Multi-assignee (new schema) with legacy single-user fallback.
  const assigneeIds = (a.assigned_user_ids && a.assigned_user_ids.length)
    ? a.assigned_user_ids
    : a.assigned_to_user_id
      ? [a.assigned_to_user_id]
      : [];
  const assignees = assigneeIds
    .map((uid) => userMap?.[uid])
    .filter(Boolean);
  const CallDirIcon =
    a.call_direction === "incoming" ? PhoneIncoming : PhoneOutgoing;
  // Calls are informational records — no strikethrough / dim styling.
  // Only planned activities that are marked done get the muted look.
  const isMuted = !isCall && a.completed;

  return (
    <li
      className={`flex items-start gap-3 px-3 py-3 ${isMuted ? "opacity-60" : ""}`}
      data-testid={`activity-row-${a.id}`}
    >
      {/* Complete toggle — hidden on call logs (they're not tasks). */}
      {isCall ? (
        <span className="mt-1 shrink-0 h-5 w-5" aria-hidden />
      ) : (
        <button
          type="button"
          onClick={onToggleCompleted}
          data-testid={`activity-toggle-${a.id}`}
          className="mt-1 shrink-0 text-zinc-400 hover:text-emerald-600"
        >
          {a.completed ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          ) : (
            <Circle className="h-5 w-5" />
          )}
        </button>
      )}

      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${typeMeta.tone}`}
      >
        <typeMeta.Icon className="h-4 w-4" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={`text-sm font-medium ${
              isMuted ? "line-through text-zinc-500" : "text-zinc-900"
            }`}
          >
            {a.title || (isCall ? "Görüşme Kaydı" : "Aktivite")}
          </span>
          {isCall && a.call_direction && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-700">
              <CallDirIcon className="h-2.5 w-2.5" />
              {a.call_direction === "incoming" ? "Gelen" : "Giden"}
            </span>
          )}
          {a.priority && a.priority !== "normal" && !isCall && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${PRIORITY_META[a.priority]?.tone || ""}`}
            >
              {PRIORITY_META[a.priority]?.label}
            </span>
          )}
          {a.reminder_enabled && !a.completed && !isCall && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
              <Bell className="h-2.5 w-2.5" />
              Hatırlatma
            </span>
          )}
          {outcome && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                outcome.category === "reached"
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-zinc-100 text-zinc-700"
              }`}
            >
              {outcome.label}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-zinc-500">
          <span>{formatDateTime(a.scheduled_at)}</span>
          {isCall && a.call_duration_minutes != null && (
            <span>· {a.call_duration_minutes} dk</span>
          )}
          {contactName && <span>· {contactName}</span>}
          {assignees.length > 0 && (
            <span className="inline-flex items-center gap-1">
              ·
              <span className="inline-flex items-center -space-x-1">
                {assignees.slice(0, 3).map((u) => (
                  <span
                    key={u.id}
                    title={u.name}
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-violet-600 text-[8px] font-semibold text-white ring-1 ring-white"
                  >
                    {nameInitials(u.name)}
                  </span>
                ))}
                {assignees.length > 3 && (
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-zinc-300 text-[8px] font-semibold text-zinc-700 ring-1 ring-white">
                    +{assignees.length - 3}
                  </span>
                )}
              </span>
              <span className="text-zinc-600">
                {assignees.length === 1
                  ? assignees[0].name
                  : `${assignees.length} kişi`}
              </span>
            </span>
          )}
        </div>
        {a.description && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-zinc-600">
            {a.description}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onDelete}
        data-testid={`activity-delete-${a.id}`}
        className="shrink-0 text-zinc-400 hover:text-red-600"
        title="Sil"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
}
